import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  api,
  onAuthEvent,
  refreshSession,
  setAccessToken,
  type StaffAuthenticated,
  type StaffProfile,
} from '@/api/client';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: Status;
  staff: StaffProfile | null;
  /** Motivo del último cierre forzado (p. ej. SESSION_REVOKED) para avisar en el login. */
  endedReason: string | null;
  signIn: (result: StaffAuthenticated) => void;
  signOut: () => Promise<void>;
  clearEndedReason: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const canal =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('cs-admin-auth') : null;

/**
 * Sesión del staff. Al montar intenta un refresh con la cookie: si existe, entra sin volver a
 * escribir la contraseña. Las pestañas se avisan con BroadcastChannel para cerrar sesión juntas.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [staff, setStaff] = useState<StaffProfile | null>(null);
  const [endedReason, setEndedReason] = useState<string | null>(null);

  const reset = useCallback(
    (reason: string | null) => {
      setAccessToken(null);
      setStaff(null);
      setStatus('anonymous');
      setEndedReason(reason);
      queryClient.clear();
    },
    [queryClient],
  );

  useEffect(() => {
    let vivo = true;
    refreshSession()
      .then((r) => {
        if (!vivo) return;
        if (r) {
          setStaff(r.staff);
          setStatus('authenticated');
        } else setStatus('anonymous');
      })
      .catch(() => vivo && setStatus('anonymous'));
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(
    () =>
      onAuthEvent((e) => {
        if (e.type === 'ended') reset(e.code);
      }),
    [reset],
  );

  useEffect(() => {
    if (!canal) return;
    const onMessage = (m: MessageEvent<{ type: string }>) => {
      if (m.data.type === 'logout') reset(null);
      if (m.data.type === 'login') {
        void refreshSession().then((r) => {
          if (r) {
            setStaff(r.staff);
            setStatus('authenticated');
          }
        });
      }
    };
    canal.addEventListener('message', onMessage);
    return () => canal.removeEventListener('message', onMessage);
  }, [reset]);

  const signIn = useCallback((result: StaffAuthenticated) => {
    setAccessToken(result.accessToken);
    setStaff(result.staff);
    setEndedReason(null);
    setStatus('authenticated');
    canal?.postMessage({ type: 'login' });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.POST('/api/v1/admin/auth/logout');
    } finally {
      canal?.postMessage({ type: 'logout' });
      reset(null);
    }
  }, [reset]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      staff,
      endedReason,
      signIn,
      signOut,
      clearEndedReason: () => setEndedReason(null),
    }),
    [status, staff, endedReason, signIn, signOut],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de <AuthProvider>');
  return ctx;
}
